import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Grid, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Button } from '@mui/material';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import { Link } from 'react-router-dom';
import { printReport, textCol, numCol } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';

export default function TurnoverReport() {
  const { t } = useTranslation();
  const [contracts, setContracts] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    apiService().get('workerContract').then((r) => r?.success && setContracts(r.data||[]));
  }, []);

  const months = ['Jan','Shk','Mar','Pri','Maj','Qer','Kor','Gus','Shta','Tet','Nen','Dhj'];
  const monthLabels = months;

  const data = useMemo(() => {
    // Build monthly active counts and separations
    const sep = Array(12).fill(0);
    const head = Array(12).fill(0);
    const toDate = (d) => d ? new Date(d) : null;

    for (let m=0;m<12;m++) {
      const startM = new Date(year, m, 1);
      const endM = new Date(year, m+1, 0);
      let active = 0; let separated = 0;
      for (const c of (contracts||[])) {
        const s = toDate(c.StartDate || c.Start || c.ContractStartDate);
        const e = toDate(c.EndDate || c.End || c.ContractEndDate);
        if (s && s <= endM && (!e || e >= startM)) active++;
        if (e && e >= startM && e <= endM) separated++;
      }
      head[m] = active;
      sep[m] = separated;
    }
    const avgHead = head.reduce((a,b)=>a+b,0) / 12 || 1;
    const rate = sep.reduce((a,b)=>a+b,0) / avgHead; // annual turnover
    return { sep, head, rate };
  }, [contracts, year]);

  const percent = (v) => (v*100).toFixed(1) + '%';

  function exportCsv(){
    const headers=[t('report.month'),t('report.active'),t('report.separations'),t('report.turnover')];
    const lines=[headers.join(',')];
    data.head.forEach((h,i)=>{
      const row=[monthLabels[i], h, data.sep[i], h? (data.sep[i]/h).toFixed(3) : 0];
      lines.push(row.join(','));
    });
    lines.push([t('report.annual'), Math.round(data.head.reduce((a,b)=>a+b,0)/12), data.sep.reduce((a,b)=>a+b,0), (data.rate||0).toFixed(3)].join(','));
    const csv=lines.join('\r\n');
    const blob=new Blob([csv],{type:'text/csv;charset=utf-8;'});
    const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='turnover.csv'; a.click(); URL.revokeObjectURL(url);
  }

  function exportPdf() {
    const pdfRows = data.head.map((h, i) => ({
      _month: monthLabels[i],
      _active: h,
      _sep: data.sep[i],
      _rate: h ? `${((data.sep[i] / h) * 100).toFixed(1)}%` : '0.0%',
    }));
    printReport({
      title: t('reports.turnover'),
      subtitle: `${t('report.year')} ${year} — ${t('report.turnoverSubtitle')}`,
      orientation: 'portrait',
      columns: [
        textCol(t('report.month'), '_month', '30%'),
        numCol(t('report.active'), '_active', '22%'),
        numCol(t('report.separations'), '_sep', '22%'),
        { label: t('report.turnoverPct'), key: '_rate', align: 'right', width: '26%' },
      ],
      rows: pdfRows,
      totals: { _active: Math.round(data.head.reduce((a,b)=>a+b,0)/12), _sep: data.sep.reduce((a,b)=>a+b,0), _rate: `${(data.rate*100).toFixed(1)}%` },
      period: String(year),
      badge: { label: `${t('report.annualRate')}: ${(data.rate*100).toFixed(1)}%`, color: data.rate > 0.15 ? '#DC2626' : '#16A34A' },
    });
  }

  return (
    <ReportPage>
      <PageHeader title={t('reports.turnover')} subtitle={t('report.turnoverSubtitle')}
        actions={<Button component={Link} to="/home/reports" variant="outlined">{t('report.backLabel')}</Button>} />
      <Card sx={{ mb:2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={2}>
              <TextField select fullWidth label={t('report.year')} value={year} onChange={(e)=>setYear(Number(e.target.value))}>
                {Array.from({length:6},(_,i)=> new Date().getFullYear()-i).map((y)=>(<MenuItem key={y} value={y}>{y}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm="auto"><ExportMenu onCsv={exportCsv} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('report.month')}</TableCell>
                <TableCell align="right">{t('report.active')}</TableCell>
                <TableCell align="right">{t('report.separations')}</TableCell>
                <TableCell align="right">{t('report.turnoverMonthly')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.head.map((h,i)=> (
                <TableRow key={i}>
                  <TableCell>{monthLabels[i]}</TableCell>
                  <TableCell align="right">{h}</TableCell>
                  <TableCell align="right">{data.sep[i]}</TableCell>
                  <TableCell align="right">{percent(h ? data.sep[i]/h : 0)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell><b>{t('report.annual')}</b></TableCell>
                <TableCell align="right"><b>{Math.round(data.head.reduce((a,b)=>a+b,0)/12)}</b></TableCell>
                <TableCell align="right"><b>{data.sep.reduce((a,b)=>a+b,0)}</b></TableCell>
                <TableCell align="right"><b>{percent(data.rate)}</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.turnover')}
        subtitle={`${t('report.year')} ${year}`}
        columns={[
          { label: t('report.month'), key: '_month', align: 'left' },
          { label: t('report.active'), key: '_active', align: 'right' },
          { label: t('report.separations'), key: '_sep', align: 'right' },
          { label: t('report.turnoverPct'), key: '_rate', align: 'right' },
        ]}
        rows={data.head.map((h, i) => ({
          _month: monthLabels[i],
          _active: h,
          _sep: data.sep[i],
          _rate: h ? `${((data.sep[i] / h) * 100).toFixed(1)}%` : '0.0%',
        }))}
        workers={[]}
      />
    </ReportPage>
  );
}
